/**
 * DURACAO — o unico lugar que converte entre unidade de dado e `HH:MM` (spec r4-01).
 *
 * O cliente pediu: "trocar formato de tempo de 0.0 para HH:MM em todo o sistema, em
 * estudos e simulados, de 0 a 24 horas". Ate aqui cada tela fazia a propria conta:
 * `toFixed(1)}h` no Manual, "2,5 h" na Jornada, "1h30min" nos graficos, `step="0.5"` nos
 * formularios. Tres formatos para o mesmo numero — e o `0.0` do placeholder era o que o
 * aluno via.
 *
 * Nada aqui muda a UNIDADE do dado: as colunas continuam em horas decimais (`test_ted`,
 * `theorical_study_time`…), minutos (`extra_study_times.duration_minutes`) ou segundos
 * (`study_sessions`). `HH:MM` e mascara de tela; a conversao e a ultima etapa antes de
 * imprimir e a primeira depois de digitar. Somar e sempre feito na unidade de origem.
 *
 * Arredondamento: minuto mais proximo. `1.5h` -> `01:30`; `0.1167h` (7 min) -> `00:07`.
 * Ida e volta `hours -> HH:MM -> hours` so muda o que esta abaixo do minuto.
 *
 * Sem JSX, sem estado, sem mutacao.
 */

/** Texto exibido quando a duracao nao veio (nulo, ausente ou nao numerico). */
export const EMPTY_DURATION = '—'

/** Teto de um unico registro de tempo — a faixa "0 a 24 horas" da reuniao. */
export const MAX_DURATION_HOURS = 24
/**
 * Teto da META TED DA SEMANA (r6-33): 7 x 24. Vale so nesse campo, porque la o numero e
 * uma SOMA da semana, nao um registro — e e o mesmo teto que o backend ja aplica em
 * `Goal::MAX_WEEK_MINUTES`. Todo outro campo de duracao continua parando em 24:00.
 */
export const MAX_WEEK_HOURS = 7 * MAX_DURATION_HOURS
export const MINUTES_PER_HOUR = 60
export const SECONDS_PER_MINUTE = 60

/** Mensagem de faixa, montada a partir do teto do campo. */
export function durationRangeMessage(maxHours: number = MAX_DURATION_HOURS): string {
  return `O tempo deve ficar entre 00:00 e ${String(maxHours).padStart(2, '0')}:00.`
}

/** Mensagem unica de faixa: a mesma em todo formulario, e a mesma que o backend devolve. */
export const DURATION_RANGE_MESSAGE = durationRangeMessage()
export const DURATION_FORMAT_MESSAGE = 'Informe o tempo como HH:MM (ex.: 01:30).'

/** `hh:mm` a `hhh:mm`; minutos sempre com dois digitos. O teto de horas e regra a parte. */
const HHMM_PATTERN = /^(\d{1,3}):([0-5]\d)$/

/** Quantos digitos de hora o campo aceita: 2 ate 99 horas, 3 a partir de 100. */
function hourDigits(maxHours: number): number {
  return String(Math.trunc(maxHours)).length
}

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value)
    return Number.isFinite(n) ? n : null
  }
  return null
}

// ---------------------------------------------------------------------------
// Exibicao
// ---------------------------------------------------------------------------

/**
 * Minutos inteiros -> `HH:MM`. Nao limita em 24: um total semanal de 30 horas imprime
 * `30:00`, e e isso mesmo — o teto vale para UM registro, nao para uma soma.
 */
export function minutesToHHMM(minutes: number): string {
  const total = Math.max(0, Math.round(minutes))
  const hours = Math.floor(total / MINUTES_PER_HOUR)
  const rest = total % MINUTES_PER_HOUR
  return `${String(hours).padStart(2, '0')}:${String(rest).padStart(2, '0')}`
}

/** Horas decimais -> `HH:MM`, arredondando ao minuto. `1.5` -> `01:30`. */
export function hoursToHHMM(hours: number): string {
  return minutesToHHMM(hours * MINUTES_PER_HOUR)
}

/** Segundos -> `HH:MM`, arredondando ao minuto. */
export function secondsToHHMM(seconds: number): string {
  return minutesToHHMM(seconds / SECONDS_PER_MINUTE)
}

/**
 * Horas decimais (ou nulo/ausente/string numerica) -> `HH:MM` ou `—`.
 *
 * E a funcao que as TELAS chamam. Aceita string porque coluna `decimal` do Rails vira
 * string no JSON; nunca devolve `NaN:NaN`.
 */
export function formatHoursHHMM(hours: unknown): string {
  const n = toFiniteNumber(hours)
  return n === null ? EMPTY_DURATION : hoursToHHMM(n)
}

/** Minutos (ou nulo/ausente) -> `HH:MM` ou `—`. */
export function formatMinutesHHMM(minutes: unknown): string {
  const n = toFiniteNumber(minutes)
  return n === null ? EMPTY_DURATION : minutesToHHMM(n)
}

// ---------------------------------------------------------------------------
// Entrada
// ---------------------------------------------------------------------------

/**
 * Mascara do campo: so digitos, no maximo quatro, com o `:` colocado pela funcao.
 *
 * Duas formas de digitar, as duas aceitas:
 * - so digitos, como num relogio: `1` -> `1`, `13` -> `13`, `130` -> `13:0`, `0130` -> `01:30`;
 * - com o `:` do aluno: `1:` -> `1:`, `1:3` -> `1:3`, `1:30` -> `1:30`.
 *
 * Qualquer outro caractere e ignorado. Nao valida faixa — isso e `parseHHMM`, no
 * blur/submit, para nao brigar com quem esta no meio da digitacao.
 */
export function maskHHMM(raw: string, maxHours: number = MAX_DURATION_HOURS): string {
  const hh = hourDigits(maxHours)
  const colonAt = raw.indexOf(':')
  if (colonAt >= 0) {
    const typedHours = raw.slice(0, colonAt).replace(/\D/g, '').slice(0, hh)
    const typedMinutes = raw.slice(colonAt + 1).replace(/\D/g, '')
    // Minuto com mais de dois digitos num campo de tres horas: o digito que sobrou pela
    // ESQUERDA e hora, nao minuto. E o que deixa `16:80` + `0` virar `168:00` enquanto o
    // aluno digita, sem precisar apagar nada. No campo de duas horas nada disso acontece:
    // `hh` e 2 e o minuto continua truncado, como na r4-01.
    if (hh > 2 && typedMinutes.length > 2 && typedHours.length < hh) {
      const move = Math.min(hh - typedHours.length, typedMinutes.length - 2)
      return `${typedHours}${typedMinutes.slice(0, move)}:${typedMinutes.slice(move, move + 2)}`
    }
    return `${typedHours}:${typedMinutes.slice(0, 2)}`
  }

  // Sem `:` digitado, o preenchimento e sempre pela esquerda com DUAS horas, como na
  // r4-01 — `1230` -> `12:30`. A terceira hora entra pelo caminho acima.
  const digits = raw.replace(/\D/g, '').slice(0, hh + 2)
  if (digits.length <= 2) return digits
  return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`
}

/**
 * Completa o que o aluno deixou pela metade ao sair do campo: `1` -> `01:00`,
 * `1:` -> `01:00`, `1:3` -> `01:03`, `01:30` -> `01:30`. Vazio continua vazio, e texto
 * sem hora (`:30`) volta como esta — `parseHHMM` e quem recusa.
 */
export function completeHHMM(raw: string, maxHours: number = MAX_DURATION_HOURS): string {
  const masked = maskHHMM(raw, maxHours)
  if (masked === '') return ''
  const [hours, minutes = ''] = masked.split(':')
  if (hours === '') return masked
  return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`
}

export interface ParsedDuration {
  /** Minutos inteiros; `null` quando vazio ou invalido. */
  minutes: number | null
  /** `null` quando vazio ou valido. Texto pronto para a tela. */
  error: string | null
  empty: boolean
}

/**
 * `HH:MM` -> minutos, com a validacao de formato e de faixa (00:00 a 24:00 inclusivo).
 *
 * Vazio NAO e erro: e "nao informado", e cada formulario decide se o campo e obrigatorio.
 */
export function parseHHMM(raw: string, maxHours: number = MAX_DURATION_HOURS): ParsedDuration {
  const trimmed = raw.trim()
  if (trimmed === '') return { minutes: null, error: null, empty: true }

  const match = HHMM_PATTERN.exec(trimmed)
  if (!match) return { minutes: null, error: DURATION_FORMAT_MESSAGE, empty: false }

  const minutes = Number(match[1]) * MINUTES_PER_HOUR + Number(match[2])
  if (minutes > maxHours * MINUTES_PER_HOUR) {
    return { minutes: null, error: durationRangeMessage(maxHours), empty: false }
  }
  return { minutes, error: null, empty: false }
}

/** `HH:MM` -> horas decimais; `null` se vazio ou invalido. `01:30` -> `1.5`. */
export function hhmmToHours(raw: string, maxHours: number = MAX_DURATION_HOURS): number | null {
  const { minutes } = parseHHMM(raw, maxHours)
  return minutes === null ? null : minutes / MINUTES_PER_HOUR
}

/** `HH:MM` -> minutos inteiros; `null` se vazio ou invalido. */
export function hhmmToMinutes(raw: string, maxHours: number = MAX_DURATION_HOURS): number | null {
  return parseHHMM(raw, maxHours).minutes
}
